import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../UserRole/CreateRole.css';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const CreateRolePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState([]);
  const [roleName, setRoleName] = useState('');
  const [formList, setFormList] = useState([{ section: '', subSections: [] }]);
  const [sections, setSections] = useState([]);
  const dropdownRefs = useRef([]);
  const token = localStorage.getItem('auth-token');

  const selectedSection = (section) => sections.find((sec) => sec.name === section);
  const selectedSections = formList.map((form) => form.section);

  const baselineRef = useRef(null);
  const roleId = location.state?.roleId || null;

  const buildSubmitPayload = (rlName = roleName, fl = formList) => {
    const privilegeList2 = fl.reduce((acc, form) => {
      if (form.section && (form.subSections?.length || 0) > 0) {
        acc[form.section] = form.subSections;
      }
      return acc;
    }, {});
    return {
      roleId: roleId || null,
      roleName: rlName || '',
      privilegeList2,
    };
  };

  useEffect(() => {
    if (location.state) {
      const { roleName: rn, sectionsAndSubsections } = location.state;
      setRoleName(rn || '');
      const nextForms = (sectionsAndSubsections || []).map((item) => ({
        section: item.section,
        subSections: item.subsections || [],
      }));
      setFormList(nextForms.length ? nextForms : [{ section: '', subSections: [] }]);
      setDropdownOpen((sectionsAndSubsections || []).map(() => false));

      const initPayload = (sectionsAndSubsections || []).reduce((acc, it) => {
        if (it.section && Array.isArray(it.subsections) && it.subsections.length) {
          acc[it.section] = it.subsections;
        }
        return acc;
      }, {});
      baselineRef.current = {
        roleId: roleId || null,
        roleName: rn || '',
        privilegeList2: initPayload,
      };
    } else {
      baselineRef.current = buildSubmitPayload('', [{ section: '', subSections: [] }]);
    }
  }, [location.state]);

  useEffect(() => {
    setDropdownOpen(formList.map(() => false));
  }, [formList]);

  useEffect(() => {
    const fetchPrivileges = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_CREATE_ROLE, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.data?.success) {
          const privileges = response.data.data.privileges || {};
          const sectionsArray = Object.entries(privileges).map(([key, value]) => ({
            name: key,
            subsections: value,
          }));
          setSections(sectionsArray);
        }
      } catch {
      }
    };
    fetchPrivileges();
  }, [token]);

  const handleSectionChange = (index, selectedOption) => {
    const newFormList = [...formList];
    newFormList[index].section = selectedOption?.value || '';
    newFormList[index].subSections = [];
    setFormList(newFormList);
  };

  const handleSubSectionSelect = (index, selectedOptions) => {
    const selectedSubSections = selectedOptions ? selectedOptions.map((option) => option.value) : [];
    const newFormList = [...formList];
    newFormList[index].subSections = selectedSubSections;
    setFormList(newFormList);
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Role name is required!',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const hasValidSection = formList.some((form) => form.section && form.subSections.length > 0);
    if (!hasValidSection) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'At least one section with subsections is required!',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const newPayload = buildSubmitPayload();
    const oldPayload = baselineRef.current || {};

    const { digest, html } = buildDiffPreview(oldPayload, newPayload);

    if (roleId && !digest.length) {
      await Swal.fire({
        icon: 'info',
        title: 'Nothing to update',
        text: 'No differences detected.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }

    const confirm = await Swal.fire({
      title: roleId ? 'Apply these changes?' : 'Create this role?',
      html: digest.length
        ? html
        : '<div style="text-align:left; font-size:13px">Creating a new role with the selected privileges.</div>',
      showCancelButton: true,
      confirmButtonText: roleId ? 'Update' : 'Create',
    });
    if (!confirm.isConfirmed) return;

    const payloadForApi = {
      roleName: newPayload.roleName,
      privilegeList2: newPayload.privilegeList2,
    };

    try {
      const apiUrlBase = process.env.REACT_APP_API_NEW_ROLE;
      const apiUrl = roleId ? `${apiUrlBase}?roleId=${roleId}` : apiUrlBase;

      const response = await axios.post(apiUrl, payloadForApi, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.data?.success) {
        fireAudit({
          entity: 'role',
          affected_id: roleId || null,
          action: roleId ? 'update' : 'create',
          businessApi: apiUrl,
          businessMethod: 'POST',
          oldPayload,
          newPayload,
          section: 'User Role',
          subSection: 'CreateRole',
          extra: { page: 'CreateRolePage' },
        });
        navigate('/RoleList');
        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: roleId ? 'Role updated successfully!' : 'Role created successfully!',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });

        baselineRef.current = newPayload;

    
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response.data?.success_message || 'Failed to save role.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.response?.data?.success_message || error?.message || 'Something went wrong',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const handleAddMore = () => {
    setFormList([...formList, { section: '', subSections: [] }]);
  };

  const handleRemove = (index) => {
    const newFormList = formList.filter((_, formIndex) => formIndex !== index);
    const newDropdownOpen = dropdownOpen.filter((_, dropIndex) => dropIndex !== index);
    setFormList(newFormList);
    setDropdownOpen(newDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefs.current.every((ref) => ref.current && !ref.current.contains(event.target))
      ) {
        setDropdownOpen(formList.map(() => false));
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [formList]);

  dropdownRefs.current = formList.map((_, i) => dropdownRefs.current[i] || React.createRef());

  return (
    <div>
      <div className="enrollment-container">
        <div className="breadcrumb">
          <Link to="/RoleList" className="breadcrumb-link">Role List</Link> &gt; {roleId ? 'Edit Role' : 'New Role'}
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="text"
            name="roleName"
            className={`walkin-form ${roleName ? 'has-value' : ''}`}
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            placeholder="Enter role name"
            required
          />
          {roleName && <label htmlFor="roleName">Role Name</label>}
        </div>

        {formList.map((form, index) => {
          const subsections = selectedSection(form.section)?.subsections || [];
          const availableSections = sections.filter(
            (sec) => !selectedSections.includes(sec.name) || sec.name === form.section
          );

          return (
            <form key={index} className="role-form">
              {index > 0 && (
                <button
                  type="button"
                  className="remove-button"
                  onClick={() => handleRemove(index)}
                >
                  ✖
                </button>
              )}

              <div className="floating-select-form" style={{ width: '98%' }}>
                <div className="dropdown-container">
                  <Select
                    name={`section-${index}`}
                    value={
                      availableSections.find((sec) => sec.name === form.section)
                        ? { value: form.section, label: form.section }
                        : null
                    }
                    onChange={(selectedOption) => selectedOption && handleSectionChange(index, selectedOption)}
                    options={availableSections.map((sec) => ({
                      value: sec.name,
                      label: sec.name,
                    }))}
                    isClearable
                    menuPortalTarget={document.body}
                    className={`walkin-form-dropdown ${form.section ? 'has-value' : ''}`}
                    classNamePrefix="walkin-select"
                    placeholder="Select Section"
                    styles={{
                      placeholder: (provided) => ({ ...provided, color: 'black' }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({ ...provided, padding: '5px' }),
                    }}
                  />
                  {form.section && <label htmlFor={`section-${index}`}>Section</label>}
                </div>
              </div>

              {form.section && (
                <div className="floating-select-form" style={{ width: '98%' }}>
                  <div ref={dropdownRefs.current[index]} className="dropdown-container">
                    <Select
                      isMulti
                      options={subsections.map((sub) => ({ label: sub, value: sub }))}
                      value={form.subSections.map((sub) => ({ label: sub, value: sub }))}
                      onChange={(selectedOptions) => handleSubSectionSelect(index, selectedOptions)}
                      placeholder="Select Subsections"
                      closeMenuOnSelect={false}
                      menuPortalTarget={document.body}
                      className={`walkin-form-dropdown-role ${form.subSections.length ? 'has-value' : ''}`}
                      classNamePrefix="walkin-select"
                      styles={{
                        placeholder: (provided) => ({ ...provided, color: 'black' }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (provided) => ({ ...provided, padding: '5px' }),
                      }}
                    />
                    {form.subSections.length > 0 && (
                      <label htmlFor={`subSections-${index}`}>Subsections</label>
                    )}
                  </div>
                </div>
              )}
            </form>
          );
        })}

        {formList.length < sections.length && (
          <div className="button-container">
            <button type="button" onClick={handleAddMore} className="add-more-button">
              Add More
            </button>
          </div>
        )}

        <div className="button-container">
          <button type="button" onClick={handleSave} className="submit-button">
            Save Role
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateRolePage;
